import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../data/hooks/auth'
import { useAuthStore } from '../../data/stores/auth.store'
import { APP_ROUTES } from '../../../constants/routes'

export default function CheckAuth() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { session } = useAuthStore()
  const { refreshToken, refreshTokenLoading, refreshTokenError, refreshTokenSuccess, signOut } = useAuth()
  const [hasAttemptedRefresh, setHasAttemptedRefresh] = useState(false)

  const redirectBackUrl = params.get('redirectUri')

  // Redirect back with base64-encoded idToken or action param
  const redirect = useCallback(
    (withToken = false, action?: string) => {
      if (!redirectBackUrl) return

      try {
        // Build redirect URL with token query param
        const url = new URL(redirectBackUrl)

        if (withToken && session?.idToken) {
          // Base64 encode the idToken
          const encodedToken = btoa(session.idToken)
          url.searchParams.set('token', encodedToken)
        }

        if (action) {
          url.searchParams.set('action', action)
        }

        // Redirect to the target URL
        setTimeout(() => {
          window.location.href = url.toString()
        }, 2000)
      } catch (error) {
        console.error('Failed to redirect with token:', error)
        navigate(APP_ROUTES.SignIn)
      }
    },
    [redirectBackUrl, session?.idToken, navigate],
  )

  // Handle authentication check - only on mount
  useEffect(() => {
    // Load session from cookies
    refreshToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (refreshTokenLoading) return

    if (refreshTokenError) {
      if (redirectBackUrl) {
        // Redirect back with sign_out action
        redirect(false, 'sign_out')
        return
      }
      window.location.replace(APP_ROUTES.SignOut)
      return
    }

    if (refreshTokenSuccess) {
      setHasAttemptedRefresh(true)
      return
    }
  }, [refreshTokenLoading, refreshTokenError, refreshTokenSuccess, redirect, signOut, redirectBackUrl])

  useEffect(() => {
    // Only redirect when refresh token was successful
    if (refreshTokenSuccess && hasAttemptedRefresh && session?.idTokenDecoded?.exp) {
      const currentTime = Math.floor(Date.now() / 1000)
      if (session.idTokenDecoded.exp > currentTime) {
        // Token is valid, redirect with token
        redirect(true)
      } else {
        // Token expired, redirect to sign in
        redirect(false)
      }
    }
  }, [refreshTokenSuccess, hasAttemptedRefresh, redirect, session?.idTokenDecoded?.exp])

  return (
    <div className="flex h-screen w-full items-center justify-center" style={{ backgroundColor: 'rgb(246, 247, 247)' }}>
      <div className="text-center">
        <img className="mx-auto" src="logo_light.png" alt="Logo" width={120} height={120} />
        <p className="text-lg font-medium text-gray-700">
          Authorizing
          <span className="inline-flex">
            <span className="animate-[fade_1.5s_ease-in-out_0s_infinite]">.</span>
            <span className="animate-[fade_1.5s_ease-in-out_0.5s_infinite]">.</span>
            <span className="animate-[fade_1.5s_ease-in-out_1s_infinite]">.</span>
          </span>
        </p>
        <p className="text-gray-700">
          {session?.idTokenDecoded?.exp && new Date(session.idTokenDecoded.exp * 1000).toLocaleString()}
        </p>
        <style>{`
          @keyframes fade {
            0%, 100% { opacity: 0; }
            50% { opacity: 1; }
          }
        `}</style>
      </div>
    </div>
  )
}
